import React, { useState } from 'react';
import { useSendReceiptMutation, SendReceiptRequest, SendReceiptResponse } from '@@api/queries/paymentQueries';
import { appConfig } from '@@config/appConfig';
import { ReceiptCard } from './ReceiptCard';
import { ReceiptSentCard } from './ReceiptSentCard';

interface PaymentSuccessfulCardProps {
  paymentId?: string | null;
}

const PaymentSuccessfulCard = ({ paymentId }: PaymentSuccessfulCardProps) => {
  const [receiptSent, setReceiptSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getPaymentId = () => {
    if (paymentId) return paymentId;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('paymentid');
  };

  const handleFinish = () => {
    window.location.href = appConfig.paymentSuccessRedirectUrl;
  };

  const { mutate: sendReceiptMutation, isLoading } = useSendReceiptMutation({
    mutationFn: async ({ paymentId, email }: SendReceiptRequest): Promise<SendReceiptResponse> => {
      const response = await fetch(`${appConfig.apiBaseURL}/api/nets/receipt/${paymentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to request receipt');
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }

      return { success: true };
    },
    onSuccess: () => {
      setReceiptSent(true);
      setError(null);
    },
    onError: (error) => {
      console.error('Error sending receipt:', error);
      setError('Failed to send receipt. Please try again.');
    },
  });

  const handleSendReceipt = (email: string) => {
    const id = getPaymentId();
    if (id) {
      setError(null);
      sendReceiptMutation({ paymentId: id, email });
    }
  };

  if (receiptSent) {
    return <ReceiptSentCard onFinish={handleFinish} />;
  }

  return <ReceiptCard onSendReceipt={handleSendReceipt} onFinish={handleFinish} isLoading={isLoading} error={error} />;
};

export default PaymentSuccessfulCard;
